import { Select } from 'antd';
import { Chart, type ChartData, registerables } from 'chart.js';
import { type MouseEvent, useMemo, useState } from 'react';

import { CenterFlex, Flex, Space } from '@/components/base';
import type { LegendProps } from '@/components/Chart/LegendItem/LegendItem';
import Spinner from '@/components/Common/Spinner';
import AMP_KEYS from '@/configs/amp-track-keys';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import { useSearchDetailContext } from '@/features/GlobalSearch/context/SearchDetailContext';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useColor from '@/hooks/domain/useColor';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { chartColors } from '@/styles/colors/chartColors';
import { CUSTOMER_DOMAIN } from '@/types/customerConst';
import { GPT_DOMAIN } from '@/types/gptConst';
import { track } from '@/utils/track/track';

import useCustomerSegmentFilter from '../../../../hooks/domain/useCustomerSegmentFilter';
import useFetchSearchCustomerSegmentTrendData from '../../hooks/useFetchSearchCustomerSegmentTrendData';
import DistributionChart from '../DistributionChart/DistributionChart';
import LegendList from '../DistributionChart/LegendList';
import type { DISTRIBUTION_CHART_LEGEND } from '../DistributionChart/types';
import ContentStyles from './SearchContentStyles';
import SearchDrilldownMenu from './SearchDrilldownMenu';

Chart.register(...registerables);

type Props = {
  domain: CUSTOMER_DOMAIN;
};

const { ContentBox, ContentHeader, DownloadButton } = ContentStyles;

const SearchCustomerSegment = (props: Props) => {
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const { addToFilterSingleValue } = useGlobalSearchModalAction();
  const { alias, searchResult, filterQueryFormats } = useSearchDetailContext();
  const color = useColor();
  const { attrOptions } = useCustomerSegmentFilter(props.domain);
  const { status, open, close, pos, setPos } = useModalState();

  const [selectedAttr, setSelectedAttr] = useState<FilterOptionT>(
    attrOptions[0],
  );

  const [selectedAttrValue, setSelectedAttrValue] = useState<string | null>(
    null,
  );

  const { trendLoading, trendData } = useFetchSearchCustomerSegmentTrendData({
    alias: alias,
    selectedDomain: props.domain,
    selectedDomainAttr: selectedAttr,
  });

  const handleChangeAttr = (key: string) => {
    const attr = attrOptions.find((op) => op.columnKey === key);
    if (!attr) throw new Error(`"${key}" Attribute are not exist`);
    setSelectedAttr(attr);
    track(AMP_KEYS.SEARCH_CUSTOMER_SEGMENT_SELECT, {
      domain: props.domain,
      segment: key,
    });
  };

  const chartTrendData: {
    chart: ChartData<'doughnut'>;
    legend: LegendProps[];
    assignedSum: number;
    notAssigned: number;
    total: number;
  } | null = useMemo(() => {
    if (!trendData) return null;

    const sum = trendData.buckets?.reduce((acc, t) => acc + t.count, 0) || 0;
    const notAssigned = trendData.total - sum;

    return {
      assignedSum: sum,
      legend: trendData.buckets.map((d, i) => ({
        name: d.bucket as string,
        value: d.count,
        color: chartColors[i],
        percentage: (d.count / trendData.total) * 100,
      })),
      notAssigned: notAssigned,
      total: trendData.total,
      chart: {
        labels: [...trendData.buckets.map((t) => t.bucket), 'Not assigned'],
        datasets: [
          {
            data: [...trendData.buckets.map((t) => t.count), notAssigned],
            backgroundColor: [
              ...trendData.buckets.map((t, index) => {
                if (t.bucket === 'Others') return color.gray400;
                return chartColors[index];
              }),
              color.gray300,
            ],
            borderColor: color.gray100,
          },
        ],
      },
    };
  }, [color, trendData]);

  const onClickLegendItem = (
    e: MouseEvent,
    value: string | DISTRIBUTION_CHART_LEGEND | null,
  ) => {
    if (typeof value !== 'string') return;
    setSelectedAttrValue(value);
    setPos({
      top: e.clientY,
      left: e.clientX,
    });
    open();
  };

  const onViewFeedbackListHandler = async (
    e: MouseEvent,
    initialOpen?: boolean,
  ) => {
    e.stopPropagation();
    close();
    if (!selectedAttrValue) return;
    const segment = selectedAttrValue.toString();
    const filterPayload = {
      [selectedAttr.columnKey]: structuredFilterUtils.buildSingleValue(segment),
    };

    setNewModal<FeedbackBlockListModalOptions>({
      alias,
      type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
      idx: 0,
      keys: [props.domain, segment],
      options: {
        queryKeys: [props.domain, segment],
        title: 'Feedback list',
        subTitle: `${props.domain} ${selectedAttr.columnName} : ${segment}`,
        domain: 'global-search',
        fetcher: async (
          sortStatus: NotNullableSortStatusT,
          pageParam?: null | string,
        ) => {
          const res = await service.globalSearch.fetchSearchFeedbackList(
            alias,
            {
              query: {
                cursor: pageParam,
                sortby: sortStatus?.sortBy,
                order: sortStatus?.orderStatus,
              },
              payload: filterPayload,
            },
          );

          const PAGE_SIZE = 50;

          return {
            count: res.count,
            list: res.feedbacks,
            paginate_token: res.feedbacks[PAGE_SIZE]?.alias,
          };
        },
        exportFetcher: async (reason?: string, password?: string) => {
          await service.globalSearch.exportSearchFeedbackList({
            alias: searchResult.alias,
            filterPayload: filterPayload,
            reason: reason,
            password: password,
          });
        },
        socketInfo: {
          domain: GPT_DOMAIN.GLOBAL_SEARCH,
          search: {
            alias: searchResult.alias,
            filterPayload: filterPayload,
          },
          initialState: initialOpen ? CollapseState.EXPANDED : undefined,
        },
      },
    });
  };

  const onAddToFilterHandler = async (e: MouseEvent) => {
    e.stopPropagation();
    close();
    if (!selectedAttrValue) return;
    const segment = selectedAttrValue.toString();
    await addToFilterSingleValue(searchResult, filterQueryFormats, {
      segment: {
        value: segment,
        name: segment,
      },
      columnKey: selectedAttr.columnKey,
    });
  };

  return (
    <ContentBox f={1} padding="24px">
      <Flex j="space-between" a="center">
        <Flex g={16}>
          <ContentHeader>
            {props.domain === CUSTOMER_DOMAIN.ACCOUNT && 'Account segments'}
            {props.domain === CUSTOMER_DOMAIN.CONTACT && 'Contact segments'}
          </ContentHeader>
          <Select
            size="small"
            options={attrOptions.map((op) => ({
              label: op.columnName,
              value: op.columnKey,
            }))}
            value={selectedAttr.columnKey}
            onChange={handleChangeAttr}
            style={{ width: 180 }}
            getPopupContainer={(triggerNode) => {
              return triggerNode.parentNode as HTMLDivElement;
            }}
          />
        </Flex>
        <Flex>
          <DownloadButton onClick={() => {}} />
        </Flex>
      </Flex>
      <Space h="24px" />
      {!chartTrendData || trendLoading ? (
        <CenterFlex style={{ height: 250, width: '100%' }}>
          <Spinner />
        </CenterFlex>
      ) : (
        <Flex g={64} a="flex-start">
          <DistributionChart
            dataLength={chartTrendData.legend.length}
            chartData={chartTrendData.chart}
            total={chartTrendData.total}
            label="Feedback count"
          />
          <LegendList
            legend={chartTrendData.legend}
            assignedSum={chartTrendData.assignedSum}
            notAssigned={chartTrendData.notAssigned}
            total={chartTrendData.total}
            selectedLegend={selectedAttrValue}
            onClickLegendItem={onClickLegendItem}
          />
        </Flex>
      )}
      <SearchDrilldownMenu
        pos={{
          top: pos?.top as number,
          left: pos?.left as number,
        }}
        status={status}
        close={close}
        onViewFeedbackListClick={onViewFeedbackListHandler}
        onAddToFilterClick={onAddToFilterHandler}
        onAskSynclyAIClick={(e) => onViewFeedbackListHandler(e, true)}
      />
    </ContentBox>
  );
};

export default SearchCustomerSegment;
